import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { PlusIcon } from "@heroicons/react/24/outline";
import {
  createReportGuideTemplate,
  fetchReportGuideTemplates,
  updateReportGuideTemplate,
} from "../../../../api/reportGuides";
import { fetchChecklistTypes } from "../../../../api/quality/checklistType";
import { SaveBar } from "../../../utils/SaveBar";
import { buildEdgesFromOptions, GuideFlowBuilder, GuideFlowField } from "../../../utils/guideFlow/GuideFlowBuilder";
import {
  buildFlowFromQuestions,
  normalizeReportGuideTemplate,
  normalizeFlow,
  questionsFromFlow,
} from "../reports/reportGuideFlow";

const EFFECT_NODE_DEFINITIONS = [
  { type: "report_text", titleKey: "Rapporttekst", defaultData: { effects: { report_text: "" } } },
  { type: "internal_report_text", titleKey: "Intern tekst", defaultData: { effects: { internal_report_text: "" } } },
  { type: "case_followup", titleKey: "Case oppfølging", defaultData: { effects: { case_followup_title: "", case_followup_text: "" } } },
  { type: "checklist", titleKey: "Sjekkliste", defaultData: { effects: { create_checklist: true, checklist_type_id: "" } } },
  { type: "sequel", titleKey: "Oppfølger", defaultData: { effects: { create_sequel: true } } },
];

export default function ReportGuideTemplateEditor({ reportType, generalOnly = false }) {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [checklistTypes, setChecklistTypes] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [template, setTemplate] = useState(emptyTemplate(reportType));
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [hasChanges, setHasChanges] = useState(false);
  const [saveState, setSaveState] = useState({ status: "idle", error: null });

  useEffect(() => {
    let mounted = true;

    async function loadTemplates() {
      setIsLoading(true);
      const params = {
        purpose: "field_report",
        include_inactive: true,
      };
      if (reportType?.id) {
        params.report_type_id = reportType.id;
      }
      const existingTemplates = await fetchReportGuideTemplates(params);
      if (!mounted) return;
      const normalizedTemplates = (existingTemplates || [])
        .map(normalizeTemplate)
        .filter(Boolean)
        .filter((item) => (generalOnly ? !item.report_type_id : true));
      const firstTemplate = normalizedTemplates[0] || emptyTemplate(reportType, generalOnly);
      setTemplates(normalizedTemplates);
      setTemplate(firstTemplate);
      setSelectedTemplateId(firstTemplate?.id ? String(firstTemplate.id) : "");
      setHasChanges(false);
      setSaveState({ status: "idle", error: null });
      setIsLoading(false);
    }

    loadTemplates();
    return () => {
      mounted = false;
    };
  }, [reportType?.id, generalOnly]);

  useEffect(() => {
    fetchChecklistTypes({ fields: "id,title,description", per_page: 100 })
      .then((response) => setChecklistTypes(response?.data?.data || []))
      .catch(() => setChecklistTypes([]));
  }, []);

  const updateTemplate = (patch) => {
    setHasChanges(true);
    setSaveState({ status: "idle", error: null });
    setTemplate((prev) => ({ ...prev, ...patch }));
  };

  const selectTemplate = (templateId) => {
    if (templateId === "__new__") {
      const nextTemplate = emptyTemplate(reportType, generalOnly);
      setTemplate(nextTemplate);
      setSelectedTemplateId("__new__");
      setHasChanges(false);
      setSaveState({ status: "idle", error: null });
      return;
    }

    const nextTemplate = templates.find((item) => String(item.id) === String(templateId));
    setTemplate(nextTemplate || emptyTemplate(reportType));
    setSelectedTemplateId(templateId || "");
    setHasChanges(false);
    setSaveState({ status: "idle", error: null });
  };

  const save = async () => {
    setSaveState({ status: "saving", error: null });
    const payload = prepareTemplateForSave({
      ...template,
      report_type_id: generalOnly
        ? null
        : template.report_type_id === undefined
        ? reportType?.id || null
        : template.report_type_id,
      purpose: "field_report",
    });

    const res = template.id
      ? await updateReportGuideTemplate(template.id, payload)
      : await createReportGuideTemplate(payload);

    if (res?.errors) {
      const message = Array.isArray(res.errors) ? res.errors.join(", ") : String(res.errors);
      setSaveState({ status: "error", error: message });
      toast.error(message);
      return;
    }

    const savedTemplate = normalizeTemplate(res);
    setTemplate(savedTemplate);
    setSelectedTemplateId(savedTemplate?.id ? String(savedTemplate.id) : "");
    setHasChanges(false);
    setSaveState({ status: "saved", error: null });
    setTemplates((prev) => {
      const withoutSaved = prev.filter((item) => String(item.id) !== String(savedTemplate.id));
      return [savedTemplate, ...withoutSaved];
    });
    toast.success(t("Saved"));
  };

  return (
    <div className="mt-6 rounded-xl border border-sky-100 bg-sky-50 p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">{t("Rapportguide")}</h3>
          <p className="mt-1 text-xs text-gray-600">
            {t("Konfigurer spørsmål som kan fylle rapporttekst og lagre svar for administrasjon.")}
          </p>
        </div>
        <label className="flex items-center gap-2 text-xs text-gray-700">
          <input
            type="checkbox"
            checked={template.active !== false}
            onChange={(e) => updateTemplate({ active: e.target.checked })}
            className="rounded border-gray-300 text-sky-600 focus:ring-sky-500"
          />
          {t("Aktiv")}
        </label>
      </div>

      <div className="mt-4 flex flex-col gap-2 rounded-lg border border-sky-100 bg-white p-3 sm:flex-row sm:items-end">
        <div className="min-w-0 flex-1">
          <label className="text-xs font-medium text-gray-600">{t("Velg guide")}</label>
          <select
            value={selectedTemplateId}
            onChange={(e) => selectTemplate(e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 text-sm shadow-sm focus:border-sky-500 focus:ring-sky-500"
          >
            {templates.map((item) => (
              <option key={item.id} value={item.id}>
                {item.title || `${t("Rapportguide")} #${item.id}`}
                {item.report_type_id ? "" : ` (${t("Generell")})`}
                {item.active === false ? ` (${t("Inaktiv")})` : ""}
              </option>
            ))}
            <option value="__new__">{t("Ny rapportguide")}</option>
          </select>
        </div>
        <button
          type="button"
          onClick={() => selectTemplate("__new__")}
          className="inline-flex items-center justify-center rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-sm font-medium text-sky-800 hover:bg-sky-100"
        >
          <PlusIcon className="mr-2 h-4 w-4" />
          {t("Ny guide")}
        </button>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <input
          value={template.title || ""}
          onChange={(e) => updateTemplate({ title: e.target.value })}
          placeholder={t("Guide title")}
          className="rounded-md border-gray-300 text-sm shadow-sm focus:border-sky-500 focus:ring-sky-500"
        />
        <input
          value={template.description || ""}
          onChange={(e) => updateTemplate({ description: e.target.value })}
          placeholder={t("Guide description")}
          className="rounded-md border-gray-300 text-sm shadow-sm focus:border-sky-500 focus:ring-sky-500"
        />
        {reportType?.id ? (
          <label className="flex items-center gap-2 rounded-md border border-sky-100 bg-white px-3 py-2 text-sm text-gray-700 sm:col-span-2">
            <input
              type="checkbox"
              checked={template.report_type_id === null}
              onChange={(e) =>
                updateTemplate({
                  report_type_id: e.target.checked ? null : reportType.id,
                })
              }
              className="rounded border-gray-300 text-sky-600 focus:ring-sky-500"
            />
            <span>
              {t("Generell rapportguide")}
              <span className="ml-2 text-xs text-gray-500">{t("Kan brukes på alle rapporttyper")}</span>
            </span>
          </label>
        ) : null}
      </div>

      <div className="mt-4">
        {isLoading ? (
          <p className="text-sm text-gray-500">{t("Loading...")}</p>
        ) : (
          <GuideFlowBuilder
            EffectsEditorComponent={ReportGuideEffectsEditor}
            checklistTypes={checklistTypes}
            defaultQuestionData={{ include_in_report: true }}
            effectNodeDefinitions={EFFECT_NODE_DEFINITIONS}
            guideFlowKind="field_report"
            markerIdPrefix="report-guide"
            renderQuestionExtras={({ data, onUpdateData, t: tr }) => (
              <label className="mt-2 flex items-center gap-2 text-xs text-slate-700">
                <input
                  type="checkbox"
                  checked={data.include_in_report === true}
                  onChange={(event) => onUpdateData({ include_in_report: event.target.checked })}
                />
                {tr("Ta med svar i rapporttekst")}
              </label>
            )}
            template={template}
            onChange={(flow) =>
              updateTemplate({
                settings: {
                  ...(template.settings || {}),
                  flow,
                },
              })
            }
          />
        )}
      </div>

      <SaveBar
        hasChanges={hasChanges}
        isSaving={saveState.status === "saving"}
        isSuccess={saveState.status === "saved"}
        isError={saveState.status === "error"}
        errorMessage={saveState.error}
        onSave={save}
        saveLabel={t("Lagre guide")}
      />
    </div>
  );
}

function ReportGuideEffectsEditor({ checklistTypes, effects, onChange, effectType }) {
  const { t } = useTranslation();
  const patch = (nextPatch) => onChange({ ...(effects || {}), ...nextPatch });

  if (!effectType) {
    return <ReportGuideEffectsEditorFull checklistTypes={checklistTypes} effects={effects} patch={patch} t={t} />;
  }

  switch (effectType) {
    case "report_text":
      return (
        <div className="mt-3 space-y-2">
          <GuideFlowField label={t("Rapporttekst ved valg")} value={effects.report_text || ""} onChange={(value) => patch({ report_text: value })} />
        </div>
      );
    case "internal_report_text":
      return (
        <div className="mt-3 space-y-2">
          <GuideFlowField label={t("Intern tekst ved valg")} value={effects.internal_report_text || ""} onChange={(value) => patch({ internal_report_text: value })} />
        </div>
      );
    case "case_followup":
      return (
        <div className="mt-3 space-y-2">
          <GuideFlowField label={t("Case oppfølging tittel")} value={effects.case_followup_title || ""} onChange={(value) => patch({ case_followup_title: value })} />
          <GuideFlowField label={t("Case oppfølging tekst")} type="textarea" value={effects.case_followup_text || ""} onChange={(value) => patch({ case_followup_text: value })} />
          <label className="flex items-center gap-2 text-xs text-slate-700">
            <input type="checkbox" checked={effects.must_be_rectified === true} onChange={(event) => patch({ must_be_rectified: event.target.checked })} />
            {t("Krever tiltak")}
          </label>
        </div>
      );
    case "checklist":
      return (
        <div className="mt-3 space-y-2">
          <select
            value={effects.checklist_type_id || ""}
            onChange={(event) => patch({ checklist_type_id: event.target.value || null, create_checklist: Boolean(event.target.value) })}
            className="w-full rounded-md border-gray-300 text-sm shadow-sm"
          >
            <option value="">{t("Ingen sjekkliste")}</option>
            {(checklistTypes || []).map((type) => (
              <option key={type.id} value={type.id}>
                {type.title || type.displayTitle}
              </option>
            ))}
          </select>
        </div>
      );
    case "sequel":
      return (
        <div className="mt-3 space-y-2">
          <label className="flex items-center gap-2 text-xs text-slate-700">
            <input type="checkbox" checked={effects.create_sequel === true} onChange={(event) => patch({ create_sequel: event.target.checked })} />
            {t("Opprett oppfølger")}
          </label>
          {effects.create_sequel === true ? (
            <div className="space-y-2 rounded-lg border border-sky-100 bg-white p-2">
              <GuideFlowField label={t("Oppfølger beskrivelse")} value={effects.sequel_description || ""} onChange={(value) => patch({ sequel_description: value })} />
              <GuideFlowField label={t("Oppfølger intern beskrivelse")} value={effects.sequel_internal_description || ""} onChange={(value) => patch({ sequel_internal_description: value })} />
              <select
                value={effects.sequel_checklist_type_id || ""}
                onChange={(event) => patch({ sequel_checklist_type_id: event.target.value || null, sequel_create_checklist: Boolean(event.target.value) })}
                className="w-full rounded-md border-gray-300 text-sm shadow-sm"
              >
                <option value="">{t("Ingen sjekkliste på oppfølger")}</option>
                {(checklistTypes || []).map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.title || type.displayTitle}
                  </option>
                ))}
              </select>
              <label className="flex items-center gap-2 text-xs text-slate-700">
                <input type="checkbox" checked={effects.sequel_has_agreement === true} onChange={(event) => patch({ sequel_has_agreement: event.target.checked })} />
                {t("Opprett avtale på oppfølger")}
              </label>
              {effects.sequel_has_agreement === true ? (
                <>
                  <label className="flex items-center gap-2 text-xs text-slate-700">
                    <input
                      type="checkbox"
                      checked={effects.sequel_use_current_agreement_time === true}
                      onChange={(event) => patch({ sequel_use_current_agreement_time: event.target.checked })}
                    />
                    {t("Bruk samme avtaledato og tid som nåværende rapport")}
                  </label>
                  {effects.sequel_use_current_agreement_time === true ? null : (
                    <>
                      <GuideFlowField label={t("Dato")} type="date" value={effects.sequel_agreement_date || ""} onChange={(value) => patch({ sequel_agreement_date: value })} />
                      <GuideFlowField label={t("Tid")} type="time" value={effects.sequel_agreement_time || ""} onChange={(value) => patch({ sequel_agreement_time: value })} />
                    </>
                  )}
                </>
              ) : null}
            </div>
          ) : null}
        </div>
      );
    default:
      return <ReportGuideEffectsEditorFull checklistTypes={checklistTypes} effects={effects} patch={patch} t={t} />;
  }
}

/** Per-option report effects (mix of fields) — same as legacy single editor. */
function ReportGuideEffectsEditorFull({ checklistTypes, effects, patch, t }) {
  return (
    <div className="mt-3 space-y-2">
      <GuideFlowField label={t("Rapporttekst ved valg")} value={effects.report_text || ""} onChange={(value) => patch({ report_text: value })} />
      <GuideFlowField label={t("Intern tekst ved valg")} value={effects.internal_report_text || ""} onChange={(value) => patch({ internal_report_text: value })} />
      <GuideFlowField label={t("Case oppfølging tittel")} value={effects.case_followup_title || ""} onChange={(value) => patch({ case_followup_title: value })} />
      <GuideFlowField label={t("Case oppfølging tekst")} type="textarea" value={effects.case_followup_text || ""} onChange={(value) => patch({ case_followup_text: value })} />
      <label className="flex items-center gap-2 text-xs text-slate-700">
        <input type="checkbox" checked={effects.must_be_rectified === true} onChange={(event) => patch({ must_be_rectified: event.target.checked })} />
        {t("Krever tiltak")}
      </label>
      <select
        value={effects.checklist_type_id || ""}
        onChange={(event) => patch({ checklist_type_id: event.target.value || null, create_checklist: Boolean(event.target.value) })}
        className="w-full rounded-md border-gray-300 text-sm shadow-sm"
      >
        <option value="">{t("Ingen sjekkliste")}</option>
        {(checklistTypes || []).map((type) => (
          <option key={type.id} value={type.id}>
            {type.title || type.displayTitle}
          </option>
        ))}
      </select>
      <label className="flex items-center gap-2 text-xs text-slate-700">
        <input type="checkbox" checked={effects.create_sequel === true} onChange={(event) => patch({ create_sequel: event.target.checked })} />
        {t("Opprett oppfølger")}
      </label>
      {effects.create_sequel === true ? (
        <div className="space-y-2 rounded-lg border border-sky-100 bg-white p-2">
          <GuideFlowField label={t("Oppfølger beskrivelse")} value={effects.sequel_description || ""} onChange={(value) => patch({ sequel_description: value })} />
          <GuideFlowField label={t("Oppfølger intern beskrivelse")} value={effects.sequel_internal_description || ""} onChange={(value) => patch({ sequel_internal_description: value })} />
          <select
            value={effects.sequel_checklist_type_id || ""}
            onChange={(event) => patch({ sequel_checklist_type_id: event.target.value || null, sequel_create_checklist: Boolean(event.target.value) })}
            className="w-full rounded-md border-gray-300 text-sm shadow-sm"
          >
            <option value="">{t("Ingen sjekkliste på oppfølger")}</option>
            {(checklistTypes || []).map((type) => (
              <option key={type.id} value={type.id}>
                {type.title || type.displayTitle}
              </option>
            ))}
          </select>
          <label className="flex items-center gap-2 text-xs text-slate-700">
            <input type="checkbox" checked={effects.sequel_has_agreement === true} onChange={(event) => patch({ sequel_has_agreement: event.target.checked })} />
            {t("Opprett avtale på oppfølger")}
          </label>
          {effects.sequel_has_agreement === true ? (
            <>
              <label className="flex items-center gap-2 text-xs text-slate-700">
                <input
                  type="checkbox"
                  checked={effects.sequel_use_current_agreement_time === true}
                  onChange={(event) => patch({ sequel_use_current_agreement_time: event.target.checked })}
                />
                {t("Bruk samme avtaledato og tid som nåværende rapport")}
              </label>
              {effects.sequel_use_current_agreement_time === true ? null : (
                <>
                  <GuideFlowField label={t("Dato")} type="date" value={effects.sequel_agreement_date || ""} onChange={(value) => patch({ sequel_agreement_date: value })} />
                  <GuideFlowField label={t("Tid")} type="time" value={effects.sequel_agreement_time || ""} onChange={(value) => patch({ sequel_agreement_time: value })} />
                </>
              )}
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function normalizeTemplate(template) {
  return normalizeReportGuideTemplate(template);
}

function prepareTemplateForSave(template) {
  const flow = normalizeFlow(
    template.settings?.flow || buildFlowFromQuestions(template.questions || []),
    template.questions || []
  );
  const questions = questionsFromFlow(flow, template.questions || []).map((question, index) => ({
    ...question,
    sort_order: question.sort_order ?? index,
    effects: buildQuestionEffects(question),
  }));
  const normalizedFlow = {
    ...flow,
    edges: buildEdgesFromOptions(flow),
  };

  return {
    ...template,
    settings: {
      ...(template.settings || {}),
      flow: normalizedFlow,
    },
    questions,
  };
}

function buildQuestionEffects(question) {
  return asArray(question.options)
    .filter((option) => option?.value !== undefined && option?.value !== null)
    .map((option) => ({
      when_value: option.value,
      ...(option.effects || {}),
    }))
    .filter((effect) => {
      return (
        effect.report_text ||
        effect.internal_report_text ||
        effect.case_followup_title ||
        effect.case_followup_text ||
        effect.must_be_rectified !== undefined ||
        effect.complaint !== undefined ||
        effect.checklist_type_id ||
        effect.create_checklist !== undefined ||
        effect.create_sequel !== undefined ||
        effect.sequel_description ||
        effect.sequel_internal_description ||
        effect.sequel_checklist_type_id ||
        effect.sequel_create_checklist !== undefined ||
        effect.sequel_has_agreement !== undefined ||
        effect.sequel_use_current_agreement_time !== undefined ||
        effect.sequel_agreement_date ||
        effect.sequel_agreement_time
      );
    });
}

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function emptyTemplate(reportType, generalOnly = false) {
  return {
    report_type_id: generalOnly ? null : reportType?.id || null,
    purpose: "field_report",
    title: reportType?.title ? `${reportType.title} guide` : "",
    description: "",
    active: true,
    version: 1,
    settings: {},
    questions: [],
  };
}
